import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useIsFetching, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  NAV, VISTA_A_GRUPO, VISTA_A_NODO, VISTA_A_TOP, PLACEHOLDER_A_TOP, VISTAS_SIN_FILTROS,
  VISTAS_SIN_SUCURSAL, MOBILE_PRINCIPALES, rutaDe,
  type NavNodo, type NavGroup, type Vista,
} from "../lib/nav";
import { useAuth, nombreCompleto, ROLES_MULTI_SUCURSAL } from "../lib/auth";
import { useFiltros, type OpcionRango } from "../lib/filtros";
import { useRpc } from "../lib/useRpc";
import { useContadores, type Contadores } from "../lib/useContadores";
import { ExportarProvider } from "../lib/exportar";
import { publico, enSubdirectorio } from "../lib/rutas";
import { Icono } from "./Base";
import BotonTema from "./BotonTema";
import BotonExportar from "./BotonExportar";

/** El globito rojo del menú: vacío cuando no hay pendientes, igual que setTabN. */
function Globo({ n }: { n?: number }) {
  if (!n) return null;
  return <span className="tab-n">· {n}</span>;
}

function primeraVista(n: NavNodo, puedeVer?: (v: Vista) => boolean): Vista | null {
  if (n.kind === "page") return n.vista;
  if (n.kind === "group") return n.tabs[0].vista;
  // carpeta (solo en la barra del celular): su primera pantalla que esta persona sí puede ver
  if (n.kind === "folder" && puedeVer) {
    for (const c of n.children) {
      if (c.kind === "page" && puedeVer(c.vista)) return c.vista;
      if (c.kind === "group") { const t = c.tabs.find((x) => puedeVer(x.vista)); if (t) return t.vista; }
    }
  }
  return null;
}

/** Subtítulo ("Atender hoy", "Resultados"…) antes del primer hijo visible de cada sección. */
function Seccion({ c, previo }: { c: NavNodo; previo: NavNodo | undefined }) {
  const s = "seccion" in c ? c.seccion : undefined;
  const sp = previo && "seccion" in previo ? previo.seccion : undefined;
  if (!s || s === sp) return null;
  return <div className="side-seccion">{s}</div>;
}

/** ¿Este nodo tiene al menos un destino que el usuario puede ver? */
function visible(n: NavNodo, puedeVer: (v: Vista) => boolean): boolean {
  if (n.kind === "page") return puedeVer(n.vista);
  if (n.kind === "group") return n.tabs.some((t) => puedeVer(t.vista));
  if (n.kind === "placeholder") return true; // roadmap, nunca se oculta por rol
  // una carpeta se muestra solo si tiene una pantalla REAL visible: los placeholders
  // no la abren (Finanzas, 26-oct-2026: sin el módulo, ni la carpeta se ve)
  return n.children.some((c) => c.kind !== "placeholder" && visible(c, puedeVer));
}

/** El "Próximamente" que se está mostrando: el original lo guardaba en
 *  estado.proximamenteInfo, aquí viaja en el `?id=` de la URL. */
function usePlaceholderId(vista: Vista): string | undefined {
  const [params] = useSearchParams();
  return vista === "proximamente" ? params.get("id") ?? undefined : undefined;
}

/**
 * El nodo de primer nivel donde vive la pantalla actual — activeTopId() del
 * original. Para un "Próximamente" la vista no basta: el nodo real es el del
 * `?id=`, y de ahí sale su carpeta.
 */
function topActivoDe(vista: Vista, phId: string | undefined): string | undefined {
  if (vista !== "proximamente") return VISTA_A_TOP[vista];
  return phId ? PLACEHOLDER_A_TOP[phId] : undefined;
}

/**
 * El nodo del menú que se resalta — activeNodeId() del original. Ojo: para una
 * vista que vive en un grupo devuelve el id del GRUPO, no el de la pestaña, para
 * que "Atención a CRM" siga marcado estés en Atender, Abandonados, Embudo o
 * Higiene (con NavLink/isActive solo se marcaría en la primera pestaña).
 */
function nodoActivoDe(vista: Vista, phId: string | undefined): string | undefined {
  if (vista === "proximamente") return phId;
  const grupo = VISTA_A_GRUPO[vista];
  if (grupo) return grupo.id;
  const nodo = VISTA_A_NODO[vista];
  return nodo && "id" in nodo ? nodo.id : undefined; // las pestañas (NavTab) no tienen id propio
}

function ItemNav({
  n, puedeVer, cont, nodoActivo,
}: { n: NavNodo; puedeVer: (v: Vista) => boolean; cont: Partial<Contadores>; nodoActivo: string | undefined }) {
  const nav = useNavigate();
  if (!visible(n, puedeVer)) return null;
  const clase = "side-item" + (nodoActivo === n.id ? " activo" : "");

  if (n.kind === "placeholder") {
    return (
      <button type="button" className={clase} onClick={() => nav(`/proximamente?id=${n.id}`)}>
        <Icono id={n.icon} />
        <span className="lbl">{n.star ? "★ " : ""}{n.label}</span>
      </button>
    );
  }
  const destino = primeraVista(n);
  if (!destino) return null;
  return (
    <NavLink to={`/${rutaDe(destino)}`} className={clase}>
      <Icono id={n.icon} />
      <span className="lbl">{n.label}</span>
      {n.kind === "page" && n.badge && <Globo n={cont[n.badge as keyof Contadores]} />}
    </NavLink>
  );
}

function Carpeta({
  n, puedeVer, topActivo, nodoActivo, vista, cont,
}: {
  n: NavNodo & { kind: "folder" }; puedeVer: (v: Vista) => boolean;
  topActivo: string | undefined; nodoActivo: string | undefined; vista: Vista;
  cont: Partial<Contadores>;
}) {
  const contieneActiva = topActivo === n.id;
  // arranca abierta si la pantalla actual vive adentro (toggleFolder(topId, true)
  // del original); el efecto la vuelve a abrir al navegar a otra de sus hijas,
  // pero nunca la cierra: cerrarla a mano sigue siendo decisión del usuario
  const [abierta, setAbierta] = useState(contieneActiva);
  const nav = useNavigate();

  // depende también de la vista y del nodo: si el usuario cierra la carpeta a
  // mano y se mueve a OTRA hija de la misma carpeta, contieneActiva no cambia y
  // sin esto la carpeta se quedaría cerrada con su hija resaltada adentro,
  // invisible (el original reabre en cada mostrarVista). nodoActivo cubre el
  // salto entre dos "Próximamente", donde la vista sigue siendo la misma.
  useEffect(() => {
    if (contieneActiva) setAbierta(true);
  }, [contieneActiva, vista, nodoActivo]);

  if (!visible(n, puedeVer)) return null;
  const hijos = n.children.filter((c) => visible(c, puedeVer));

  return (
    <>
      <button
        type="button"
        className={"side-item side-folder" + (abierta ? " abierto" : "") + (contieneActiva ? " activo" : "")}
        onClick={() => setAbierta((a) => !a)}
        aria-expanded={abierta}
      >
        <Icono id={n.icon} />
        <span className="lbl">{n.label}</span>
        <Icono id="i-chev" />
      </button>
      {/* la animación del CSS depende de la clase .abierto, no de desmontar */}
      <div className={"side-sub" + (abierta ? " abierto" : "")}>
        <div className="side-sub-inner">
          {hijos.map((c, i) => {
            const clase = "side-subitem" + (nodoActivo === c.id ? " activo" : "");
            if (c.kind === "placeholder") {
              return (
                <Fragment key={c.id}>
                  <Seccion c={c} previo={hijos[i - 1]} />
                  <button type="button" className={clase} onClick={() => nav(`/proximamente?id=${c.id}`)}>
                    {c.star ? "★ " : ""}{c.label}
                  </button>
                </Fragment>
              );
            }
            const d = primeraVista(c);
            if (!d) return null;
            return (
              <Fragment key={c.id}>
                <Seccion c={c} previo={hijos[i - 1]} />
                <NavLink to={`/${rutaDe(d)}`} className={clase}>
                  {c.label}
                  {c.kind === "page" && c.badge && <Globo n={cont[c.badge as keyof Contadores]} />}
                </NavLink>
              </Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
}

function SubTabs({ grupo, puedeVer, cont }: { grupo: NavGroup; puedeVer: (v: Vista) => boolean; cont: Partial<Contadores> }) {
  return (
    <div className="subtabs">
      {grupo.tabs
        .filter((t) => puedeVer(t.vista))
        .map((t) => (
          <NavLink
            key={t.vista} to={`/${rutaDe(t.vista)}`}
            className={({ isActive }) => "subtab" + (isActive ? " activo" : "")}
          >
            {t.label}
            {t.badge && <Globo n={cont[t.badge as keyof Contadores]} />}
          </NavLink>
        ))}
    </div>
  );
}

function MenuUsuario() {
  const { perfil, puedeVer, salir } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  if (!perfil) return null;
  const nombre = nombreCompleto(perfil);

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button" className="user-chip" aria-haspopup="true"
        aria-expanded={abierto} onClick={() => setAbierto((a) => !a)}
      >
        {perfil.avatar_url ? (
          <img
            src={perfil.avatar_url} alt=""
            style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          <Icono id="i-user" clase="ic ic-lg" />
        )}
        <span className="quien">
          {nombre} · {perfil.rol}{perfil.sucursal ? " · " + perfil.sucursal : ""}
        </span>
        <Icono id="i-chev" clase="ic ic-sm chev" />
      </button>
      {abierto && (
        <div className="user-panel">
          <div className="quien-full">{nombre}<br />{perfil.email}</div>
          <button type="button" className="salir" onClick={() => { setAbierto(false); nav("/perfil"); }}>
            <Icono id="i-user" /> Mi perfil
          </button>
          {puedeVer("usuarios") && (
            <button type="button" className="salir" onClick={() => { setAbierto(false); nav("/usuarios"); }}>
              <Icono id="i-userplus" /> Usuarios
            </button>
          )}
          <button type="button" className="salir" onClick={salir}>
            <Icono id="i-x" /> Salir
          </button>
        </div>
      )}
    </div>
  );
}


/** Contador rojo de la barra inferior (el espejo de los globitos del menú). */
function BadgeMovil({ n }: { n?: number }) {
  if (!n) return null;
  return <span className="badge">{n}</span>;
}

/**
 * Barra inferior de celular + hoja "Más". Portada de renderMobileNav()
 * (app.js 554-571): 4 destinos principales y el resto en la hoja.
 * Sin esto, en el teléfono el menú lateral está oculto por CSS y no hay
 * NINGUNA forma de cambiar de módulo — que es justo lo que reportó Gun.
 */
function NavMovil({
  topActivo, nodoActivo, puedeVer, cont,
}: {
  topActivo: string | undefined; nodoActivo: string | undefined;
  puedeVer: (v: Vista) => boolean; cont: Partial<Contadores>;
}) {
  const [masAbierta, setMasAbierta] = useState(false);
  // la hoja abre la carpeta donde vive la pantalla actual, igual que el menú
  // lateral; solo cabe una abierta a la vez
  const [carpeta, setCarpeta] = useState<string | null>(topActivo ?? null);
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  // si la sección actual no es de las cuatro de la barra, vive en "Más" y ese
  // botón se queda encendido para no dejar la barra sin ningún destino marcado
  const enMas = !MOBILE_PRINCIPALES.includes(topActivo ?? "");

  useEffect(() => {
    if (!masAbierta) return;
    const fuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMasAbierta(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [masAbierta]);

  useEffect(() => { setCarpeta(topActivo ?? null); }, [topActivo]);

  const ir = (v: Vista) => { setMasAbierta(false); nav(`/${rutaDe(v)}`); };
  const irPlaceholder = (id: string) => { setMasAbierta(false); nav(`/proximamente?id=${id}`); };

  const principales = MOBILE_PRINCIPALES
    .map((id) => NAV.find((n) => n.id === id))
    .filter((n): n is NavNodo => !!n && visible(n, puedeVer));
  const resto = NAV.filter((n) => !MOBILE_PRINCIPALES.includes(n.id) && visible(n, puedeVer));

  // los globitos de Atender y Calidad viven en pestañas internas; en la barra
  // el grupo aparece colapsado, así que se le espeja el número
  const badgeDe = (id: string) => (id === "ventas" ? cont.tabNAtender : undefined);

  return (
    <div ref={ref}>
      <nav className="mobile-tabbar" aria-label="Navegación móvil">
        {principales.map((n) => {
          const destino = primeraVista(n, puedeVer);
          const etiqueta = ("labelMovil" in n && n.labelMovil) ? n.labelMovil : n.label;
          return (
            <button
              key={n.id} type="button"
              className={topActivo === n.id ? "on" : undefined}
              onClick={() => destino && ir(destino)}
            >
              <Icono id={n.icon} />
              <span className="lbl">{etiqueta}</span>
              <BadgeMovil n={badgeDe(n.id)} />
            </button>
          );
        })}
        <button
          type="button" aria-haspopup="true" aria-expanded={masAbierta}
          className={masAbierta || enMas ? "on" : undefined}
          onClick={() => setMasAbierta((a) => !a)}
        >
          <Icono id="i-more" />
          <span className="lbl">Más</span>
        </button>
      </nav>

      <div className={"hoja-mas" + (masAbierta ? "" : " oculto")} role="menu">
        {resto.map((n) => {
          if (n.kind === "folder") {
            const abierta = carpeta === n.id;
            return (
              <div key={n.id}>
                <button
                  type="button"
                  className={"mas-item side-folder" + (abierta ? " abierto" : "")
                    + (topActivo === n.id ? " activo" : "")}
                  onClick={() => setCarpeta(abierta ? null : n.id)}
                  aria-expanded={abierta}
                >
                  <Icono id={n.icon} />
                  <span className="lbl">{n.label}</span>
                  <Icono id="i-chev" />
                </button>
                <div className={"side-sub" + (abierta ? " abierto" : "")}>
                  <div className="side-sub-inner">
                    {n.children.filter((c) => visible(c, puedeVer)).map((c, i, hijos) => {
                      const clase = "side-subitem mas-item" + (nodoActivo === c.id ? " activo" : "");
                      if (c.kind === "placeholder") {
                        return (
                          <Fragment key={c.id}>
                            <Seccion c={c} previo={hijos[i - 1]} />
                            <button type="button" className={clase} onClick={() => irPlaceholder(c.id)}>
                              {c.star ? "★ " : ""}{c.label}
                            </button>
                          </Fragment>
                        );
                      }
                      const d = primeraVista(c);
                      if (!d) return null;
                      return (
                        <Fragment key={c.id}>
                          <Seccion c={c} previo={hijos[i - 1]} />
                          <button type="button" className={clase} onClick={() => ir(d)}>
                            {c.label}
                          </button>
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }
          const claseItem = "side-item mas-item" + (nodoActivo === n.id ? " activo" : "");
          if (n.kind === "placeholder") {
            return (
              <button key={n.id} type="button" className={claseItem}
                onClick={() => irPlaceholder(n.id)}>
                <Icono id={n.icon} />
                <span className="lbl">{n.star ? "★ " : ""}{n.label}</span>
              </button>
            );
          }
          const destino = primeraVista(n);
          if (!destino) return null;
          return (
            <button key={n.id} type="button" className={claseItem}
              onClick={() => ir(destino)}>
              <Icono id={n.icon} />
              <span className="lbl">{n.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const OPCIONES: { v: OpcionRango; t: string }[] = [
  { v: "hoy", t: "Hoy" },
  { v: "ayer", t: "Ayer" },
  { v: "7", t: "Últimos 7 días" },
  { v: "30", t: "Últimos 30 días" },
  { v: "90", t: "Últimos 90 días" },
  { v: "custom", t: "Elegir fechas…" },
];

interface SucursalOpcion {
  id: number;
  nombre: string;
}

const MIN = 60_000;

/** El `dataUpdatedAt` más reciente de todo el caché: cuándo se refrescó por
 *  última vez algo de lo que está en pantalla. 0 = todavía no ha llegado nada. */
function ultimaActualizacion(qc: QueryClient): number {
  let max = 0;
  for (const q of qc.getQueryCache().getAll()) {
    if (q.state.dataUpdatedAt > max) max = q.state.dataUpdatedAt;
  }
  return max;
}

/**
 * "Actualizado hace X min" + botón para forzar refresco. Portado de
 * actualizarFrescuraTxt()/btnFrescura (app.js 756-765).
 * Importa de verdad en Operación: es una cola VIVA de gente esperando
 * respuesta, y sin esto la pantalla se queda congelada sin avisar que
 * lo que se ve ya es viejo.
 */
function BotonFrescura() {
  const qc = useQueryClient();
  const enVuelo = useIsFetching() > 0;
  // `tick` no guarda nada: solo provoca el re-render que recalcula el texto
  // cada 30s SIN volver a pedir datos (igual que el setInterval del original).
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // auto-refresco cada 5 min, solo con la pestaña visible: no gasta llamadas
  // mientras nadie está viendo
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void qc.invalidateQueries();
    }, 5 * MIN);
    return () => clearInterval(id);
  }, [qc]);

  const ultima = ultimaActualizacion(qc);
  let txt = "Actualizando…";
  if (ultima) {
    const min = Math.round((Date.now() - ultima) / MIN);
    txt = min < 1 ? "Actualizado justo ahora" : `Actualizado hace ${min} min`;
  }

  return (
    <button
      type="button"
      className={"filtros-meta" + (enVuelo ? " refrescando" : "")}
      title="Actualizar ahora"
      onClick={() => void qc.invalidateQueries()}
    >
      <Icono id="i-refresh" clase="ic ic-sm" />
      <span>{txt}</span>
    </button>
  );
}

export function Shell({ vista, children }: { vista: Vista; children: ReactNode }) {
  const { perfil, puedeVer } = useAuth();
  const f = useFiltros();
  const multi = !!perfil && ROLES_MULTI_SUCURSAL.includes(perfil.rol);
  // igual que cargarSucursales() del original: solo se pide para los roles
  // que de verdad pueden cambiar de sucursal
  const qSuc = useRpc<SucursalOpcion[]>("api_mis_sucursales", {}, { enabled: multi });
  const cont = useContadores();
  const grupo = VISTA_A_GRUPO[vista];
  const sinFiltros = VISTAS_SIN_FILTROS.includes(vista);
  const sinSucursal = VISTAS_SIN_SUCURSAL.includes(vista);
  const phId = usePlaceholderId(vista);
  const topActivo = topActivoDe(vista, phId);
  const nodoActivo = nodoActivoDe(vista, phId);
  const nombreSucursal =
    (qSuc.data ?? []).find((s) => s.id === f.sucursal)?.nombre ?? "sucursal";

  // si no, al saltar de una página larga a otra corta se aterriza a media pantalla.
  // phId va en las dependencias porque entre dos "Próximamente" la vista no cambia
  // Con llaves A PROPÓSITO: en Chrome reciente window.scrollTo() devuelve una
  // PROMESA, y sin llaves esa promesa se volvía la "limpieza" del efecto → React
  // la llamaba como función al cambiar de vista ("destroy is not a function").
  // Era el culpable sin nombre del 31-ago que tapa blindajeCleanup (vite.config.ts).
  useEffect(() => { window.scrollTo(0, 0); }, [vista, phId]);

  return (
    <div id="app">
      <header className="top">
        <img className="kex-logo-img" src={publico("logo-kex.png")} alt="Kitchen Experts" />
        <span className="marca-radar">· <b>Radar</b></span>
        <BotonTema />
        <MenuUsuario />
      </header>

      <div className="app-body">
        <nav className="sidebar" aria-label="Navegación principal">
          {NAV.map((n) =>
            n.kind === "folder" ? (
              <Carpeta
                key={n.id} n={n} puedeVer={puedeVer}
                topActivo={topActivo} nodoActivo={nodoActivo} vista={vista} cont={cont}
              />
            ) : (
              <ItemNav key={n.id} n={n} puedeVer={puedeVer} cont={cont} nodoActivo={nodoActivo} />
            ),
          )}
        </nav>

        {/* el registro de exportación envuelve a la barra de filtros Y a la
            vista: el botón vive en una, lo exportable se registra en la otra */}
        <ExportarProvider>
        <main>
          {!sinFiltros && (
            <>
              <div className="page-head">
                <span className="ic-radar-wrap">
                  <Icono id="i-target" clase="ic ic-lg" />
                </span>
                <div>
                  <h1>Radar KEX</h1>
                  <p>
                    Monitorea en tiempo real la atención de ventas por WhatsApp en
                    todas tus sucursales.
                  </p>
                </div>
              </div>

              <div className="filtros">
                <select
                  value={f.opcion}
                  onChange={(e) => f.setOpcion(e.target.value as OpcionRango)}
                  aria-label="Periodo"
                >
                  {OPCIONES.map((o) => (
                    <option key={o.v} value={o.v}>{o.t}</option>
                  ))}
                </select>
                {f.opcion === "custom" && (
                  <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <input
                      type="date" value={f.custom.desde} aria-label="Desde"
                      onChange={(e) => f.setCustom({ ...f.custom, desde: e.target.value })}
                    />
                    <span style={{ color: "var(--muted)" }}>→</span>
                    <input
                      type="date" value={f.custom.hasta} aria-label="Hasta"
                      onChange={(e) => f.setCustom({ ...f.custom, hasta: e.target.value })}
                    />
                  </span>
                )}
                {multi && (
                  <select
                    value={f.sucursal ?? ""}
                    onChange={(e) => f.setSucursal(e.target.value ? Number(e.target.value) : null)}
                    disabled={sinSucursal}
                    title={sinSucursal ? "Esta vista muestra datos globales, no por sucursal" : undefined}
                    aria-label="Sucursal"
                  >
                    <option value="">Todas las sucursales</option>
                    {(qSuc.data ?? []).map((s) => (
                      <option key={s.id} value={s.id}>{s.nombre}</option>
                    ))}
                  </select>
                )}
                <BotonFrescura />
                <BotonExportar
                  vista={vista}
                  sucursalNombre={f.sucursal !== null && !sinSucursal ? nombreSucursal : undefined}
                />
              </div>

              {/* pastilla de la sucursal filtrada, con su × para quitarla
                  (pintarChips): en las vistas globales no se pinta, para no
                  sugerir que el filtro aplica cuando no */}
              {f.sucursal !== null && !sinSucursal && (
                <div className="filtros-chips">
                  <span className="chip-filtro">
                    {nombreSucursal}
                    <button
                      type="button" aria-label="Quitar filtro de sucursal"
                      onClick={() => f.setSucursal(null)}
                    >
                      ×
                    </button>
                  </span>
                </div>
              )}
            </>
          )}

          {grupo && <SubTabs grupo={grupo} puedeVer={puedeVer} cont={cont} />}
          {/* La transición entre vistas (misma que PILOT): el `key` con la
              vista hace que React REMONTE este div en cada cambio de pantalla,
              y el CSS de .modulo-entra escalona la entrada de sus hijos sin
              que las vistas tengan que saber nada. */}
          <div key={vista} className="modulo-entra">
            {children}
          </div>
        </main>
        </ExportarProvider>
      </div>

      <NavMovil topActivo={topActivo} nodoActivo={nodoActivo} puedeVer={puedeVer} cont={cont} />

      {/* La cinta desaparece sola el día del switch: cuando la app viva en la
          raíz ya no es "la beta", es el Radar. */}
      {enSubdirectorio && <div className="cinta-beta">Beta</div>}
    </div>
  );
}
